import { Euler, Vector3 } from 'three';

/**
 * Přípojné body pro předměty v rukou.
 *
 * Rozdělení odpovídá tomu, jak to řeší velké enginy, a má dobrý důvod:
 *
 *  - **Socket** je vlastnost *kostry*. Popisuje, jak je natočená dlaň vůči
 *    předmětu, který v ní leží. Ladí se jednou a platí pro všechno, co
 *    postava kdy vezme do ruky.
 *  - **Grip** je vlastnost *modelu*. Říká jen, kde má ta konkrétní zbraň
 *    rukojeť a jak je velká.
 *
 * Bez toho rozdělení by se každá nová zbraň musela nastavovat od nuly a
 * každá by seděla trochu jinak — přesně ten problém, kdy má jedna nakloněnou
 * čepel a druhá trčí z pěsti.
 *
 * Doladěné hodnoty přežijí načtení stránky: panel pod F9 je ukládá do
 * prohlížeče, takže se ladění neztratí při refreshi.
 */

export interface HandSocket {
  /** Posun od středu kosti dlaně, v metrech. */
  offset: Vector3;
  /** Natočení předmětu vůči dlani. */
  rotation: Euler;
}

export interface WeaponGrip {
  /**
   * Posun modelu podél jeho vlastní osy tak, aby rukojeť seděla v socketu.
   *
   * Quaternius zbraně mají počátek u hrotu, ne u jílce — tohle číslo je
   * vzdálenost od hrotu k místu, kam patří pěst.
   */
  gripOffset: number;
  /** Zdrojové modely jsou několikametrové, tímhle se srovnají do měřítka. */
  scale: number;
}

/**
 * Socket pravé ruky. Společný pro všechny zbraně.
 *
 * Hodnoty jsou vyladěné okem v panelu pod F9 — spočítat se nedaly, protože
 * záleží na tom, jak má rig natočenou dlaň v klidové pozici.
 */
export const HAND_R_SOCKET: HandSocket = {
  offset: new Vector3(-0.024, 0.086, 0),
  rotation: new Euler(2.89, 0, 1.41),
};

/** Grip každé zbraně. Nová zbraň = jeden řádek. */
export const WEAPON_GRIPS: Record<string, WeaponGrip> = {
  Sword_Golden: { gripOffset: 0.06, scale: 0.2 },
};

const STORAGE_KEY = 'hra.attachments';

interface StoredFit {
  socket?: { offset: [number, number, number]; rotation: [number, number, number] };
  grips?: Record<string, WeaponGrip>;
}

/** Načte doladěné hodnoty z prohlížeče. Volat jednou při startu. */
export function loadAttachmentOverrides(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const stored = JSON.parse(raw) as StoredFit;

    if (stored.socket) {
      HAND_R_SOCKET.offset.fromArray(stored.socket.offset);
      HAND_R_SOCKET.rotation.set(...stored.socket.rotation);
    }
    for (const [name, grip] of Object.entries(stored.grips ?? {})) {
      WEAPON_GRIPS[name] = grip;
    }
  } catch {
    // Poškozený nebo zakázaný localStorage nesmí shodit start hry —
    // v nejhorším se ladění vrátí na výchozí hodnoty.
  }
}

/** Uloží aktuální hodnoty, aby přežily načtení stránky. */
export function saveAttachmentOverrides(): void {
  try {
    const data: StoredFit = {
      socket: {
        offset: HAND_R_SOCKET.offset.toArray() as [number, number, number],
        rotation: [HAND_R_SOCKET.rotation.x, HAND_R_SOCKET.rotation.y, HAND_R_SOCKET.rotation.z],
      },
      grips: { ...WEAPON_GRIPS },
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // viz výše
  }
}

/** Zapomene uložené ladění a vrátí se k hodnotám ze zdrojáku. */
export function clearAttachmentOverrides(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // viz výše
  }
}

/** Blok kódu k vložení do tohoto souboru. */
export function formatAttachments(weaponName: string): string {
  const r = HAND_R_SOCKET.rotation;
  const o = HAND_R_SOCKET.offset;
  const grip = WEAPON_GRIPS[weaponName];
  const n = (value: number) => Number(value.toFixed(4));

  return (
    `export const HAND_R_SOCKET: HandSocket = {\n` +
    `  offset: new Vector3(${n(o.x)}, ${n(o.y)}, ${n(o.z)}),\n` +
    `  rotation: new Euler(${n(r.x)}, ${n(r.y)}, ${n(r.z)}),\n` +
    `};\n\n` +
    `export const WEAPON_GRIPS: Record<string, WeaponGrip> = {\n` +
    `  ${weaponName}: { gripOffset: ${n(grip?.gripOffset ?? 0)}, scale: ${n(grip?.scale ?? 1)} },\n` +
    `};`
  );
}
